import { defineQuery, useQuery } from '@pinia/colada'
import { computed, ref } from 'vue'
import { coreClient } from '@/api/coreclient.ts'
import { haversineDist, type PointOfInterest, type PropertyDetailsResponse } from 'common'

export const usePropertyReportPointsOfInterest = defineQuery(() => {
  const subjectProperty = ref<PropertyDetailsResponse | null>(null)
  const comparables = ref<PropertyDetailsResponse[] | null>(null)
  const isEnabled = ref<boolean>(false)

  const compsCentroid = computed<{ lat: number; lon: number } | null>(() => {
    if (!comparables.value?.length) {
      return null
    }

    // Convert lat/lon to Cartesian coordinates to handle wraparound
    let x = 0
    let y = 0
    let z = 0

    comparables.value.forEach((comp) => {
      const latRad = (comp.lat * Math.PI) / 180
      const lonRad = (comp.lon * Math.PI) / 180

      x += Math.cos(latRad) * Math.cos(lonRad)
      y += Math.cos(latRad) * Math.sin(lonRad)
      z += Math.sin(latRad)
    })

    const total = comparables.value.length

    x /= total
    y /= total
    z /= total

    // Convert average Cartesian coordinates back to lat/lon
    const centralLon = Math.atan2(y, x)
    const centralSquareRoot = Math.sqrt(x * x + y * y)
    const centralLat = Math.atan2(z, centralSquareRoot)

    return {
      lat: (centralLat * 180) / Math.PI,
      lon: (centralLon * 180) / Math.PI
    }
  })

  const radiusMiles = computed<number>(() => {
    if (!subjectProperty.value && !comparables.value?.length) {
      return 0.25 // Default radius if no comparables or subject
    }

    let maxDistance = 0

    if (subjectProperty.value && comparables.value?.length) {
      comparables.value.forEach((comp) => {
        const distance = haversineDist(subjectProperty.value as PropertyDetailsResponse, comp)
        if (distance > maxDistance) {
          maxDistance = distance
        }
      })
    } else if (comparables.value?.length && compsCentroid.value) {
      // Calculate from bounds latlng if no subject property is specified, add buffer based on max comp distance from bounds center
      const latLng = { lat: compsCentroid.value.lat, lon: compsCentroid.value.lon }

      comparables.value.forEach((comp) => {
        const distance = haversineDist(latLng, comp)
        if (distance > maxDistance) {
          maxDistance = distance
        }
      })
    }

    // Add a buffer (20%) to ensure POIs around the furthest comparable are included
    return Math.max(maxDistance * 1.2, 1) // Minimum 1 mile radius
  })

  const mapCenter = computed(() => {
    if (subjectProperty.value) {
      return { lat: subjectProperty.value.lat, lng: subjectProperty.value.lon }
    } else if (compsCentroid.value) {
      return { lat: compsCentroid.value.lat, lng: compsCentroid.value.lon }
    }

    return undefined
  })

  const subjectId = computed(() => {
    if (subjectProperty.value) {
      return subjectProperty.value.id
    }

    return undefined
  })

  const mapCenterKey = computed(() => {
    if (mapCenter.value) {
      return JSON.stringify(mapCenter.value)
    }

    return ''
  })

  const { state, ...rest } = useQuery({
    key: () => ['points_of_interest', mapCenterKey.value, radiusMiles.value?.toString() ?? '0'],
    query: async (): Promise<PointOfInterest[]> => {
      const client = await coreClient()

      const response = await client.pointsOfInterest.pointsOfInterest(
        mapCenter.value!.lat,
        mapCenter.value!.lng,
        radiusMiles.value,
        subjectId.value!
      )

      return response
    },
    enabled: () => isEnabled.value && Boolean(mapCenter.value)
  })

  const pointsOfInterest = computed<PointOfInterest[]>(() => {
    return state.value?.data ?? []
  })

  return {
    state,
    subjectProperty,
    comparables,
    isEnabled,
    compsCentroid,
    mapCenter,
    radiusMiles,
    pointsOfInterest,
    ...rest
  }
})
